import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/App";
import { MobileLayout } from "@/components/Layout";
import { toast } from "sonner";
import { 
  Plus, 
  Calendar,
  User,
  FileText,
  MapPin,
  Clock,
  AlertTriangle,
  CheckCircle,
  Wrench,
  Loader2,
  Camera,
  Image,
  X,
  FileDown
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ["Avancement", "Observation", "Problème", "Sécurité"];
const STATUSES = ["OK", "À corriger", "Urgent", "Corrigé"];

const statusConfig = {
  "OK": { icon: CheckCircle, class: "status-ok" },
  "À corriger": { icon: Wrench, class: "status-to-fix" },
  "Urgent": { icon: AlertTriangle, class: "status-urgent" },
  "Corrigé": { icon: Clock, class: "status-fixed" }
};

const categoryConfig = {
  "Avancement": "category-avancement",
  "Observation": "category-observation",
  "Problème": "category-probleme",
  "Sécurité": "category-securite"
};

const VisitPage = () => {
  const { visitId } = useParams();
  const navigate = useNavigate();
  const [visit, setVisit] = useState(null);
  const [observations, setObservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingObs, setEditingObs] = useState(null);
  const [formData, setFormData] = useState({
    description: "",
    category: "Observation",
    status: "OK",
    responsible: "",
    due_date: "",
    location: "",
    photos: []
  });
  
  // Photo handling
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreviewModal, setPhotoPreviewModal] = useState({ open: false, url: "" });
  const cameraInputRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    loadData();
  }, [visitId]);

  const loadData = async () => {
    try {
      const [visitRes, obsRes] = await Promise.all([
        api.get(`/visits/${visitId}`),
        api.get(`/observations?visit_id=${visitId}`)
      ]);
      setVisit(visitRes.data);
      setObservations(obsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  // PDF Report generation - opens backend endpoint directly in new tab
  const handleGeneratePdf = () => {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Session expirée, veuillez vous reconnecter");
      return;
    }
    
    // Open backend endpoint directly with token as query parameter
    const pdfUrl = `${BACKEND_URL}/api/visits/${visitId}/report?token=${encodeURIComponent(token)}`;
    window.open(pdfUrl, '_blank');
    
    toast.success("Rapport PDF ouvert dans un nouvel onglet");
  };

  // Photo upload handlers
  const handleCameraCapture = () => {
    if (cameraInputRef.current) {
      cameraInputRef.current.click();
    }
  };

  const handleFileSelect = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const processPhotoUpload = async (file) => {
    if (!file) return;
    
    setUploadingPhoto(true);
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append("file", file);
      
      const response = await api.post("/upload/photo", formDataUpload, {
        headers: {
          "Content-Type": "multipart/form-data"
        }
      });
      
      const photoUrl = response.data.url;
      setFormData(prev => ({
        ...prev,
        photos: [...prev.photos, photoUrl]
      }));
      
      toast.success("Photo ajoutée");
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("Erreur lors de l'upload de la photo");
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handlePhotoChange = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      await processPhotoUpload(file);
    }
    // Reset input
    e.target.value = "";
  };

  const removePhoto = (indexToRemove) => {
    setFormData(prev => ({
      ...prev,
      photos: prev.photos.filter((_, index) => index !== indexToRemove)
    }));
  };

  const getFullPhotoUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    // Handle both /api/photos/ and /uploads/ formats
    if (url.startsWith("/uploads/")) {
      // Convert old format to new API format
      const filename = url.replace("/uploads/", "");
      return `${BACKEND_URL}/api/photos/${filename}`;
    }
    return `${BACKEND_URL}${url}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingObs) {
        await api.put(`/observations/${editingObs.id}`, formData);
        toast.success("Observation mise à jour");
      } else {
        await api.post("/observations", {
          visit_id: visitId,
          ...formData
        });
        toast.success("Observation créée");
      }
      closeModal();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const openEditModal = (obs) => {
    setEditingObs(obs);
    setFormData({
      description: obs.description,
      category: obs.category,
      status: obs.status,
      responsible: obs.responsible || "",
      due_date: obs.due_date || "",
      location: obs.location || "",
      photos: obs.photos || []
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingObs(null);
    setFormData({
      description: "",
      category: "Observation",
      status: "OK",
      responsible: "",
      due_date: "",
      location: "",
      photos: []
    });
  };

  if (loading) {
    return (
      <MobileLayout title="Chargement..." showBack>
        <div className="flex items-center justify-center h-64">
          <div className="spinner"></div>
        </div>
      </MobileLayout>
    );
  }

  return (
    <MobileLayout title="Visite" showBack>
      <div className="px-4 lg:px-6 py-6 space-y-6 animate-fade-in">
        {/* Visit Info */}
        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2 text-orange-500">
            <Calendar size={18} />
            <span className="font-semibold text-lg">{visit?.date}</span>
          </div>
          <div className="flex items-center gap-2 text-zinc-300">
            <User size={16} />
            <span className="text-sm">{visit?.visitor_name}</span>
          </div>
          {visit?.general_notes && (
            <div className="pt-2 border-t border-zinc-800">
              <div className="flex items-start gap-2 text-zinc-400">
                <FileText size={16} className="mt-0.5" />
                <span className="text-sm">{visit.general_notes}</span>
              </div>
            </div>
          )}
          
          {/* PDF Report Button */}
          <div className="pt-3 border-t border-zinc-800">
            <Button
              onClick={handleGeneratePdf}
              className="w-full bg-zinc-800 hover:bg-zinc-700 text-white h-12 font-semibold"
              data-testid="generate-pdf-btn"
            >
              <FileDown size={18} className="mr-2" />
              Générer le rapport PDF
            </Button>
          </div>
        </div>

        {/* Observations */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-sm text-zinc-400 uppercase tracking-wider">
              Observations ({observations.length})
            </h3>
          </div>

          {observations.length === 0 ? (
            <div className="empty-state py-8">
              <FileText size={40} className="mx-auto mb-3 text-zinc-600" />
              <p className="text-zinc-400 mb-4">Aucune observation</p>
              <Button onClick={() => setShowModal(true)} className="btn-primary" data-testid="create-first-observation">
                <Plus size={18} className="mr-2" />
                Ajouter une observation
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {observations.map((obs) => {
                const StatusIcon = statusConfig[obs.status]?.icon || CheckCircle;
                const hasPhotos = obs.photos && obs.photos.length > 0;
                
                return (
                  <button
                    key={obs.id}
                    onClick={() => openEditModal(obs)}
                    className="card-interactive w-full p-4 text-left"
                    data-testid={`observation-item-${obs.id}`}
                  >
                    <div className="space-y-3">
                      {/* Photos preview row */}
                      {hasPhotos && (
                        <div className="flex gap-2 overflow-x-auto pb-2">
                          {obs.photos.slice(0, 3).map((photo, idx) => (
                            <div 
                              key={idx} 
                              className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-zinc-800"
                            >
                              <img 
                                src={getFullPhotoUrl(photo)} 
                                alt={`Photo ${idx + 1}`}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ))}
                          {obs.photos.length > 3 && (
                            <div className="flex-shrink-0 w-16 h-16 rounded-lg bg-zinc-800 flex items-center justify-center">
                              <span className="text-zinc-400 text-sm font-medium">
                                +{obs.photos.length - 3}
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Header with category and status */}
                      <div className="flex items-center justify-between gap-2">
                        <span className={`badge ${categoryConfig[obs.category]}`}>
                          {obs.category}
                        </span>
                        <span className={`badge ${statusConfig[obs.status]?.class}`}>
                          <StatusIcon size={12} className="mr-1" />
                          {obs.status}
                        </span>
                      </div>

                      {/* Description */}
                      <p className="text-white text-sm">{obs.description}</p>

                      {/* Meta info */}
                      <div className="flex flex-wrap gap-3 text-xs text-zinc-500">
                        {obs.location && (
                          <div className="flex items-center gap-1">
                            <MapPin size={12} />
                            <span>{obs.location}</span>
                          </div>
                        )}
                        {obs.responsible && (
                          <div className="flex items-center gap-1">
                            <User size={12} />
                            <span>{obs.responsible}</span>
                          </div>
                        )}
                        {obs.due_date && (
                          <div className="flex items-center gap-1">
                            <Clock size={12} />
                            <span>{obs.due_date}</span>
                          </div>
                        )}
                        {hasPhotos && (
                          <div className="flex items-center gap-1 text-orange-500">
                            <Camera size={12} />
                            <span>{obs.photos.length} photo{obs.photos.length > 1 ? 's' : ''}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* FAB */}
      <button
        onClick={() => setShowModal(true)}
        className="fab"
        data-testid="add-observation-fab"
      >
        <Plus size={24} className="text-white" />
      </button>

      {/* Create/Edit Observation Modal */}
      <Dialog open={showModal} onOpenChange={closeModal}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md mx-4 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white text-xl">
              {editingObs ? "Modifier l'observation" : "Nouvelle observation"}
            </DialogTitle>
            <p className="text-zinc-400 text-sm">
              {editingObs ? "Modifiez les détails de l'observation" : "Commencez par prendre une photo"}
            </p>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            {/* PHOTO SECTION - First for mobile-first workflow */}
            <div className="space-y-3">
              <Label className="text-zinc-300 text-sm uppercase tracking-wider">
                Photos
              </Label>
              
              {/* Photo action buttons */}
              <div className="flex gap-2">
                <Button
                  type="button"
                  onClick={handleCameraCapture}
                  disabled={uploadingPhoto}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white h-14"
                  data-testid="camera-btn"
                >
                  {uploadingPhoto ? (
                    <Loader2 className="animate-spin" size={20} />
                  ) : (
                    <>
                      <Camera size={22} className="mr-2" />
                      Prendre photo
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  onClick={handleFileSelect}
                  disabled={uploadingPhoto}
                  className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white h-14"
                  data-testid="gallery-btn"
                >
                  <Image size={22} className="mr-2" />
                  Galerie
                </Button>
              </div>
              
              {/* Hidden file inputs */}
              <input
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoChange}
                className="hidden"
                data-testid="camera-input"
              />
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                onChange={handlePhotoChange}
                className="hidden"
                data-testid="file-input"
              />
              
              {/* Photo previews */}
              {formData.photos.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {formData.photos.map((photo, index) => (
                    <div 
                      key={index} 
                      className="relative aspect-square rounded-lg overflow-hidden bg-zinc-800 group"
                    >
                      <img 
                        src={getFullPhotoUrl(photo)} 
                        alt={`Photo ${index + 1}`}
                        className="w-full h-full object-cover cursor-pointer"
                        onClick={() => setPhotoPreviewModal({ open: true, url: getFullPhotoUrl(photo) })}
                      />
                      <button
                        type="button"
                        onClick={() => removePhoto(index)}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        data-testid={`remove-photo-${index}`}
                      >
                        <X size={14} className="text-white" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {formData.photos.length === 0 && (
                <p className="text-zinc-500 text-xs text-center py-2">
                  Aucune photo ajoutée
                </p>
              )}
            </div>

            <div className="border-t border-zinc-800 pt-4 space-y-4">
              <div className="space-y-2">
                <Label className="text-zinc-300 text-sm uppercase tracking-wider">
                  Description *
                </Label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Décrivez l'observation..."
                  rows={3}
                  required
                  className="input-dark w-full resize-none py-3"
                  data-testid="obs-description-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-zinc-300 text-sm uppercase tracking-wider">
                    Catégorie
                  </Label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="select-dark w-full"
                    data-testid="obs-category-select"
                  >
                    {CATEGORIES.map((cat) => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label className="text-zinc-300 text-sm uppercase tracking-wider">
                    Statut
                  </Label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="select-dark w-full"
                    data-testid="obs-status-select"
                  >
                    {STATUSES.map((status) => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300 text-sm uppercase tracking-wider">
                  Localisation
                </Label>
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Zone A, Étage 2..."
                  className="input-dark"
                  data-testid="obs-location-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300 text-sm uppercase tracking-wider">
                  Responsable
                </Label>
                <Input
                  value={formData.responsible}
                  onChange={(e) => setFormData({ ...formData, responsible: e.target.value })}
                  placeholder="Nom du responsable"
                  className="input-dark"
                  data-testid="obs-responsible-input"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300 text-sm uppercase tracking-wider">
                  Date limite
                </Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="input-dark"
                  data-testid="obs-duedate-input"
                />
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                onClick={closeModal}
                className="btn-secondary flex-1"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={saving}
                className="btn-primary flex-1"
                data-testid="obs-submit-btn"
              >
                {saving ? <Loader2 className="animate-spin" size={20} /> : (editingObs ? "Modifier" : "Créer")}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Photo Preview Modal */}
      <Dialog open={photoPreviewModal.open} onOpenChange={(open) => setPhotoPreviewModal({ open, url: "" })}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg p-2">
          <img 
            src={photoPreviewModal.url} 
            alt="Preview" 
            className="w-full h-auto rounded-lg"
          />
        </DialogContent>
      </Dialog>
    </MobileLayout>
  );
};

export default VisitPage;
